import {Mapper} from '@s-ui/domain'

export class SeoAdsListMetaInformationMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Function} deps.seoDataValueObjectFactory
   * @param {SeoDataMapper} deps.seoDataMapper
   * @param {SeoLiteralMapper} seoLiteralMapper
   * @param {VehicleStructuredDataSeoMapper} deps.vehicleStructuredDataSeoMapper
   * @param {BreadcrumbListStructuredDataSeoMapper} deps.breadcrumbListStructuredDataSeoMapper
   * @param {WebPageStructuredDataSeoMapper} deps.webPageStructuredDataSeoMapper
   * @param {SeoAdsListSemanticMetaConfigMapper} deps.seoAdsListSemanticMetaConfigMapper
   * @param {Object} deps.config
   * */
  constructor({
    seoDataValueObjectFactory,
    seoDataMapper,
    seoLiteralMapper,
    seoAdsListSemanticMetaConfigMapper,
    vehicleStructuredDataSeoMapper,
    breadcrumbListStructuredDataSeoMapper,
    webPageStructuredDataSeoMapper,
    config
  }) {
    super()
    this._seoDataValueObjectFactory = seoDataValueObjectFactory
    this._seoDataMapper = seoDataMapper
    this._seoLiteralMapper = seoLiteralMapper
    this._seoAdsListSemanticMetaConfigMapper = seoAdsListSemanticMetaConfigMapper
    this._vehicleStructuredDataSeoMapper = vehicleStructuredDataSeoMapper
    this._breadcrumbListStructuredDataSeoMapper = breadcrumbListStructuredDataSeoMapper
    this._webPageStructuredDataSeoMapper = webPageStructuredDataSeoMapper
    this._config = config
    this._seoConfig = this._config.get('seo')
    this._listingSectionIds = this._config.get('COMMON').LISTING_SECTION_IDS
  }

  /**
   * Parse SEO Canonical URL replacing all needed variables within them.
   * @method
   */
  _parseCanonical = ({url, placeholderValues = {}}) =>
    Object.keys(placeholderValues).reduce((url, key) => {
      const placeholder = `%{${key}}`
      const replacement = placeholderValues[key] || ''
      return url.replace(new RegExp(placeholder, 'g'), replacement)
    }, url)

  _getCheapLiteral = ({culture, maxPrice}) => {
    if (maxPrice > 0 && maxPrice <= this._seoConfig.MAX_CHEAP_PRICE) return this._seoConfig.cheapLiteral[culture]
  }

  _getLitresLiteral = ({culture, litres}) => {
    if (litres) {
      const {litresLiteral} = this._seoConfig
      if (!litresLiteral) return

      return litresLiteral[culture]?.replace('%{litres}', litres)
    }
  }

  _getToponymLiteral = ({city, culture, province, region}) => {
    const {locatedInLiteral} = this._seoConfig

    const hasCity = Boolean(city)
    const hasRegion = Boolean(region)
    const hasProvince = Boolean(province)

    if (!hasRegion && !hasProvince) return

    let toponym
    if (hasCity) toponym = city
    if (!hasCity && hasRegion) toponym = region
    if (!hasCity && !hasRegion && hasProvince) toponym = province

    return locatedInLiteral[culture]?.replace('%{location}', toponym)
  }

  _getPaginationLiteral = ({culture, page}) => ` - ${this._seoConfig.paginationLiteral[culture]} ${page}`

  // TODO: remove default value after tests are updated (bodyTypeIds field should be mandatory)
  _getFirstBodyTypeId = ({bodyTypeIds = []}) => bodyTypeIds[0]

  _bodyTypeValueEnhancer = (searchParams, mutationCases) => {
    const {path, culture, bodyType} = searchParams
    if (!bodyType) return // TODO: temp check because right now can be optional from the outside (MTR-56077)

    const firstBodyTypeId = this._getFirstBodyTypeId(searchParams)
    const shouldReplaceBodyType = Boolean(mutationCases[firstBodyTypeId])
    if (!shouldReplaceBodyType) return searchParams.bodyType

    return mutationCases[firstBodyTypeId].urls
      ? this._bodyTypeInExperimentValue({
          path,
          culture,
          bodyType,
          mutationCases: mutationCases[firstBodyTypeId]
        })
      : mutationCases[firstBodyTypeId].literal[culture]
  }

  _bodyTypeInExperimentValue = ({path, culture, bodyType, mutationCases}) => {
    return mutationCases.urls.includes(path) ? mutationCases.literal[culture] : bodyType
  }

  _typeValueHandlerForBasedOnFields = (searchParams, dependsOn, isSemantic) => {
    let updatedTypePlaceholder = ''

    // TODO: 1. Automate
    // TODO: 2. Normalize/split real search params and literals
    const firstBodyTypeId = this._getFirstBodyTypeId(searchParams)
    const shouldUpdateBasedOnBodyType = dependsOn.bodyType && dependsOn.bodyType[firstBodyTypeId]
    const shouldUpdateBasedOnCategoryId =
      dependsOn.categoryId && dependsOn.categoryId[searchParams.categoryId] && searchParams.version && isSemantic

    updatedTypePlaceholder = shouldUpdateBasedOnBodyType ? dependsOn.bodyType[firstBodyTypeId] : updatedTypePlaceholder

    updatedTypePlaceholder = shouldUpdateBasedOnCategoryId
      ? dependsOn.categoryId[searchParams.categoryId]
      : updatedTypePlaceholder

    return updatedTypePlaceholder || searchParams.type
  }

  _typeValueEnhancer = (searchParams, mutationCases, isSemantic) => {
    const hasToMutateBasedOnDifferentField = Boolean(Object.keys(mutationCases.dependsOn).length)

    return hasToMutateBasedOnDifferentField
      ? this._typeValueHandlerForBasedOnFields(searchParams, mutationCases.dependsOn, isSemantic)
      : searchParams.type
  }

  _enhancePlaceholdersValues = ({searchParams, isSemantic}) => {
    const updatedPlaceholders = {}
    const {MUTABLE_PLACEHOLDERS} = this._config.get('seo')

    const fieldsEnhancersCallbacks = {
      bodyType: this._bodyTypeValueEnhancer,
      type: this._typeValueEnhancer
    }

    MUTABLE_PLACEHOLDERS.forEach(mutableField => {
      const concreteFieldCallback = fieldsEnhancersCallbacks[mutableField.name]
      if (!concreteFieldCallback) return

      const concreteFieldCallbackResult = concreteFieldCallback(searchParams, mutableField.mutationCases, isSemantic)

      if (concreteFieldCallbackResult) updatedPlaceholders[mutableField.name] = concreteFieldCallbackResult
    })

    return updatedPlaceholders
  }

  _getSellerType = ({culture, sellerTypeId}) => {
    return Boolean(sellerTypeId) && this._seoConfig.SELLER_TYPES_LITERALS[culture][sellerTypeId]
  }

  _getPlaceholdersValues = ({culture, searchParams, isSemantic}) => {
    const {
      bodyType,
      canonical,
      categoryId,
      category2,
      color,
      city,
      fuelType,
      litres,
      make,
      model,
      maxPrice = {},
      nResults,
      page,
      province,
      region,
      seats,
      sellerTypeId,
      version,
      year
    } = searchParams
    const ADS_LIST_TITLES = this._seoConfig.ADS_LIST_TITLES
    const type = ADS_LIST_TITLES[categoryId][culture]

    return {
      bodyType,
      canonical,
      category2,
      cheap: this._getCheapLiteral({culture, maxPrice: maxPrice.value}),
      color: color?.toLowerCase(),
      fuelType,
      litres: this._getLitresLiteral({culture, litres}),
      make,
      model,
      nResults,
      pagination: Boolean(page) && this._getPaginationLiteral({culture, page}),
      seats,
      toponym: this._getToponymLiteral({city, culture, province, region}),
      type,
      sellerType: this._getSellerType({culture, sellerTypeId}),
      version: version && version.toUpperCase(),
      year,
      maxPrice: maxPrice.text,
      ...this._enhancePlaceholdersValues({
        searchParams: {
          ...searchParams,
          type,
          culture
        },
        isSemantic
      })
    }
  }

  _getTitlePlaceholdersValues = ({placeholderValues, province}) => {
    const hasProvince = Boolean(province)
    const showResultsInTitle = hasProvince

    const {nResults} = placeholderValues

    return {
      ...placeholderValues,
      nResults: showResultsInTitle ? nResults : null
    }
  }

  /**
   * @method
   */
  async map({culture, params = {}, preloadImages = []}) {
    const {
      bodyType,
      breadcrumb,
      category2,
      categoryId,
      entry,
      environmentalLabel,
      environmentalLabelValue,
      filteredOfferTypeId,
      fuelType,
      highPrice,
      isCrawlableAndIndexable,
      listingSectionId,
      lowPrice,
      make,
      maxKms,
      maxPrice = {},
      model,
      nResults,
      offerTypeId: paramOfferTypeId,
      path,
      province,
      seats,
      sellerTypeId,
      transmission,
      version,
      year
    } = params

    const adsListMetaMapper = this._seoConfig.adsListMetaMapper
    const {CRAWLABLE_AND_INDEXABLE_META_ROBOTS, NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS} = this._seoConfig

    const {
      MOTOS_CATEGORY1_ID,
      OFFER_TYPE_NEW,
      SEGUNDA_MANO_CATEGORY1_ID,
      UNSELECTED_OFFER_TYPE,
      VANS_AND_COMMERCIAL_CATEGORY1_ID,
      AUTOCARAVANS_AND_TRAILERS_CATEGORY1_ID
    } = this._config.get('search')

    const {CHEAP_NEW_VEHICLE_PRICE} = this._config.get('vehicles')

    const getOfferTypeId = () => {
      if (paramOfferTypeId === UNSELECTED_OFFER_TYPE) return filteredOfferTypeId

      return filteredOfferTypeId === OFFER_TYPE_NEW ? filteredOfferTypeId : paramOfferTypeId
    }

    const offerTypeId = getOfferTypeId()

    const _getAdsListMetaConfig = ({categoryId, category2, offerTypeId}) => {
      const hasMake = Boolean(make)
      const hasBodyType = Boolean(bodyType)
      const hasMaxPrice = Boolean(maxPrice)
      const hasCategory2 = Boolean(category2)
      const isOfferTypeNew = offerTypeId === OFFER_TYPE_NEW
      const isCheapNewVehicle = hasMaxPrice && isOfferTypeNew && maxPrice.value === CHEAP_NEW_VEHICLE_PRICE
      const hasSeats = Boolean(seats)
      const hasVersion = Boolean(version)

      const getCochesMetaConfig = () => {
        if (hasMake) return 'make'
        if (hasBodyType) return 'bodyType'
        if (isCheapNewVehicle) return 'cheap_new'
        if (hasVersion) return 'version'
        return 'default'
      }
      const getMotosMetaConfig = () => (isOfferTypeNew && hasMake ? 'default' : 'empty')

      const getCommercialVansMetaConfig = () => {
        if (hasMake) return 'makeModel'
        if (hasSeats) return 'seats'
        if (hasVersion) return 'version'
        return 'default'
      }

      const getAutocaravansAndTrailersMetaConfig = () => (hasCategory2 ? 'category2' : 'default')

      const getMetaConfigByCategoryIdMap = {
        [SEGUNDA_MANO_CATEGORY1_ID]: getCochesMetaConfig,
        [MOTOS_CATEGORY1_ID]: getMotosMetaConfig,
        [VANS_AND_COMMERCIAL_CATEGORY1_ID]: getCommercialVansMetaConfig,
        [AUTOCARAVANS_AND_TRAILERS_CATEGORY1_ID]: getAutocaravansAndTrailersMetaConfig
      }

      return getMetaConfigByCategoryIdMap[categoryId] && getMetaConfigByCategoryIdMap[categoryId]()
    }

    const _getAdsListSemanticMetaConfig = () =>
      Boolean(path) &&
      this._seoAdsListSemanticMetaConfigMapper.map({
        bodyType,
        entry,
        environmentalLabel,
        environmentalLabelValue,
        fuelType,
        make,
        maxPrice: maxPrice.text,
        model,
        path,
        province,
        sellerTypeId,
        version,
        year
      })

    const maybeAdsListSemanticMetaConfig = _getAdsListSemanticMetaConfig()

    const isSemantic = Boolean(maybeAdsListSemanticMetaConfig)

    const adsListMetaConfig = isSemantic
      ? maybeAdsListSemanticMetaConfig
      : _getAdsListMetaConfig({
          categoryId,
          category2,
          filteredOfferTypeId,
          listingSectionId,
          offerTypeId
        })

    const adsListMetaConfigObject = adsListMetaMapper[categoryId][offerTypeId][adsListMetaConfig]

    /**
     * If there is not a config for this ads-list configuration
     * Page will use 'default' configuration
     */
    const adsListPageConfig = adsListMetaConfigObject
      ? adsListMetaConfigObject[culture]
      : adsListMetaMapper[categoryId][offerTypeId].default[culture]

    const {H1, TITLE, META_DESCRIPTION, META_ROBOTS, CANONICAL} = adsListPageConfig

    const placeholderValues = this._getPlaceholdersValues({
      culture,
      searchParams: params,
      isSemantic
    })

    const parsedH1 = this._seoLiteralMapper.setText({text: H1}).setPlaceholderValues({placeholderValues}).map()

    const parsedTitle = this._seoLiteralMapper
      .setText({text: TITLE})
      .setPlaceholderValues({
        placeholderValues: this._getTitlePlaceholdersValues({
          placeholderValues,
          province
        })
      })
      .map()

    const parsedDescription = this._seoLiteralMapper
      .setText({text: META_DESCRIPTION})
      .setPlaceholderValues({
        placeholderValues
      })
      .map()

    const parsedCanonical = this._parseCanonical({
      url: CANONICAL,
      placeholderValues
    })

    const {DEFAULT_AREA_SERVED} = this._seoConfig

    const areaServed = province || params.city || params.region || DEFAULT_AREA_SERVED[culture]

    const webPageStructuredDataVO = this._webPageStructuredDataSeoMapper.map({
      canonical: parsedCanonical,
      name: parsedTitle
    })

    const hasBreadcrumb = breadcrumb && breadcrumb.length > 0
    const breadcrumbStructuredDataVO =
      hasBreadcrumb &&
      this._breadcrumbListStructuredDataSeoMapper.map({
        breadcrumb,
        id: `${parsedCanonical}#breadcrumb`
      })

    const vehicleStructuredDataVO = this._vehicleStructuredDataSeoMapper.map({
      name: parsedH1,
      make,
      model,
      lowPrice,
      highPrice,
      nResults,
      canonical: parsedCanonical,
      fuelType,
      transmission,
      bodyType,
      environmentalLabel,
      maxKms,
      areaServed,
      offerTypeId: paramOfferTypeId
    })

    const defaultMetaRobots =
      (!nResults && !isCrawlableAndIndexable) || listingSectionId === this._listingSectionIds.ALL_VEHICLES
        ? NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS
        : CRAWLABLE_AND_INDEXABLE_META_ROBOTS

    const parsedMetaRobots = this._seoLiteralMapper
      .setText({text: META_ROBOTS})
      .setPlaceholderValues({
        placeholderValues: {
          metaRobots: defaultMetaRobots
        }
      })
      .map()

    const {links, metas, scripts, title} = this._seoDataMapper
      .title(parsedTitle)
      .description(parsedDescription)
      .robots(parsedMetaRobots)
      .canonical(parsedCanonical)
      .addSchemaData(webPageStructuredDataVO)
      .addSchemaData(breadcrumbStructuredDataVO)
      .addSchemaData(vehicleStructuredDataVO)
      .useGraphFormat()
      .setPreloadImages({preloadImages})
      .setOpenGraphData({
        title: parsedTitle,
        description: parsedDescription,
        url: parsedCanonical
      })
      .setTwitterCardsData()
      .map()

    return this._seoDataValueObjectFactory({
      h1: parsedH1,
      head: {
        link: links,
        meta: metas,
        scripts,
        title
      }
    })
  }
}
